
/* -----------------------------------------------------------------------------------------------------
  - Created: [20-02-2025] by [SREERAJ]
  - Modified: [YYYY-MM-DD] 
  - Description: This file contains the Employee Report search filter component
------------------------------------------------------------------------------------------------------- */

import React, { useEffect, useState } from 'react';
import Grid from '@mui/material/Grid2';
import { useFormik } from 'formik';
import dayjs from 'dayjs';
import '../../ReportsCommonStyles/CommonSearchFilterStyle.css';
import ActionButton from '../../../../Components/custom_components/CustomButtons/ActionButton/ActionButton';
import CustomTextFieldWithIcon from '../../../../Components/custom_components/custom_text_field/CustomTextFieldWithIcon';
import CustomBreadcrumbs from '../../../../Components/custom_components/CustomBreadcrumbs/CustomBreadcrumbs';
import { get_worksite_with_out_pagination } from '../../../../config/Worksite/siteApis';
import CustomSelectDropdown from '../../../../Components/custom_components/CustomSelectDropdown/CustomSelectDropdown';
import ActionButtonWithIcon from '../../../../Components/custom_components/CustomButtons/ActionButtonWithIcon/ActionBtnWithIcon';
import { Icons } from '../../../../utils/GlobalIcons';
import { get_project_with_out_pagination } from '../../../../config/Worksite/projectApis';
import { monthOptions, uptoCurrentyearOptions, yearOptions } from '../../../../utils/formatDate';

function InvoiceReportFilter({
  tableTitle,
  searchFilter,
  on_change_value, on_page_count_update,
  onExportPDF,
  onExportExcel,
  isExportingPDF,
  isExportingExcel
}) {

  const [worksiteOptions, setWorksiteOptions] = useState([]);
  const [projectListing, SetProjectListing] = useState([]);

  const [selectedYear, setSelectedYear] = useState('');
  const [selectedMonth, setSelectedMonth] = useState('');

  useEffect(() => {
    fetchProjects()
    fetchSites();
  }, []);

  // FETCH PROJECTS
  const fetchProjects = async () => {
    try {
      const response = await get_project_with_out_pagination();
      if (response?.status === "success") {
        // Transform data into the desired format with label and value
        const transformedData = response.data.data?.map((item) => ({
          label: item?.ProjectName,
          value: item?.ProjectID
        }));

        // Set the transformed data to the state
        SetProjectListing(transformedData || []);
      } else {
        SetProjectListing([]);
      }
    } catch (error) {
      console.error('Error fetching :', error);
      SetProjectListing([]);
    }
  };


  const fetchSites = async () => {
    try {
      const response = await get_worksite_with_out_pagination();
      if (response?.status === 'success') {
        const filtered = response.data.data
          .filter(item => item?.ParentWorksiteCode !== null)
          .map(item => ({ label: item.worksiteName, value: item.WorksiteID }));
        setWorksiteOptions(filtered);
      }
    } catch (error) {
      console.error('Failed to load worksites', error);
    }
  };


  const formik = useFormik({
    initialValues: {
      invoice_no: '',
      client: '',
      worksite: '',
      projectId: '',
      month: '', year: ''
    },

  });

  const resetFilter = () => {
    formik.resetForm();

    on_change_value(1)
    on_page_count_update(25)

    setSelectedMonth('');
    setSelectedYear('');

    searchFilter({
      invoice_no: '',
      client: '',
      worksite: '',
      projectId: '',
      month: '', year: ''
    });
  };
  const breadcrumbs = ["Reports", tableTitle];

  return (
    <div className="common-search-filter-container">
      <div className="common-search-filter-sub-container">
        <div className="common-search-header">
          <div className="common-search-left">
            <div className="common-table-title">{tableTitle}</div>
            <CustomBreadcrumbs breadcrumbs={breadcrumbs} />
          </div>
          <div className="common-export-buttons-container">
            <ActionButtonWithIcon
              label="Export PDF"
              onClick={onExportPDF}
              icon={Icons.PdfIcon}
              backgroundColor="#ffffff"
              hoverColor="#fef2f2"
              labelColor="#000000"
              borderColor="#e5e7eb"
              iconColor="#dc2626"
              loading={isExportingPDF}
            />

            <ActionButtonWithIcon
              label="Export Excel"
              onClick={onExportExcel}
              icon={Icons.ExcelIcon}
              backgroundColor="#ffffff"
              hoverColor="#f0fdf4"
              labelColor="#000000"
              borderColor="#e5e7eb"
              iconColor="#16a34a"
              loading={isExportingExcel}
            />
          </div>
        </div>

        <Grid container spacing={3} alignItems="center">

          <Grid item xs={12} sm={6} md={3}>
            <CustomTextFieldWithIcon
              id="invoice_no"
              name="invoice_no"
              placeholder="Search by Invoice no"
              value={formik.values.invoice_no}
              onChange={formik.handleChange}
              width="200px"

            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <CustomTextFieldWithIcon
              id="client"
              name="client"
              placeholder="Search by Client"
              value={formik.values.client}
              onChange={formik.handleChange}
              width="200px"

            />
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <CustomSelectDropdown
              name="projectId"
              placeholder="Select Project"
              options={projectListing}
              value={projectListing?.find(option => option?.value === formik?.values?.projectId) || ""}
              onChange={(selectedOption) => {
                formik.setFieldValue("projectId", selectedOption ? selectedOption.value : "");
              }}
              onBlur={formik.handleBlur}
              width="200px"
              isMulti={false} // Set to false for single-select
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <CustomSelectDropdown
              name="worksite"
              placeholder="Select Worksite"
              options={worksiteOptions}
              value={worksiteOptions.find(opt => opt.value === formik.values.worksite) || ''}
              onChange={opt => formik.setFieldValue('worksite', opt?.value || '')}
              onBlur={formik.handleBlur}
              isClearable={true}
              width="200px"
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>

            <CustomSelectDropdown
              name="month"
              placeholder="Select Month"
              options={monthOptions} //  Reusable array
              value={
                monthOptions.find((m) => m.value === selectedMonth)
              }
              onChange={(selectedOption) => {
                setSelectedMonth(selectedOption?.value || "");
              }}
              isMulti={false}
              isClearable={false}
              width="200px"
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <CustomSelectDropdown
              name="year"
              placeholder="Select Year"
              options={yearOptions} // Reusable array
              value={
                yearOptions.find((yr) => yr.value === selectedYear)
              }
              onChange={(selectedOption) => {
                setSelectedYear(selectedOption?.value || ""); // Handle empty / "All"
              }}
              isMulti={false}
              isClearable={false}
              width="200px"
            />
          </Grid>

          <Grid item xs={12} sm={12} md={3}>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <ActionButton
                  onClick={() => {
                    on_change_value(1)
                    searchFilter({ ...formik.values, month: selectedMonth, year: selectedYear }); // Always trigger search, even if no values changed
                  }}
                  btnDisable={false}
                  label="Search"
                  btnType="primary"
                  fullWidth
                />
              </Grid>
              <Grid item xs={6}>
                <ActionButton
                  onClick={resetFilter}
                  label="Reset"
                  btnType="outlined"
                  fullWidth
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </div>
    </div>
  );
}

export default InvoiceReportFilter;